import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { logger } from "@wayflyer/blackbox-fe";
import type { FundingApplicationSchemas } from "api-types";
import Cookies from "js-cookie";
import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { FullScreenLoader, getFinancingStageQueryKey, useStrictParams } from "shared-ui";
import { CUSTOMER_APP_CONFIG_QUERY_KEY, useCustomerAppConfig } from "undercarriage";
import { axiosClient } from "auth-sdk";

import { FUNDING_APPLICATION_CONFIGURE_NEW_FUNDER_CUSTOMER_API } from "../../components/constants";
/**
 * Route to create a new SALES_MCA for first time customer that will be redirected here from Company creation
 * Should not be used from anywhere else and if a customer arrives here with past advances or product requests
 * they will be redirected to home
 */
export const ConfigureNewFunderCustomerPage = () => {
  const { customerDomain } = useStrictParams<{ customerDomain: string }>();
  const fundingApi = FUNDING_APPLICATION_CONFIGURE_NEW_FUNDER_CUSTOMER_API.replace(":customerSlug", customerDomain);

  // Check customer status
  const { data: statusData } = useSuspenseQuery({
    queryKey: [customerDomain, "configure-new-funder-customer", "status"],
    queryFn: () => axiosClient.get<FundingApplicationSchemas.ConfigureNewFunderCustomerStatus>(`${fundingApi}status`),
    select: ({ data }) => data,
  });

  if (statusData.has_previous_advances) {
    logger.warn(
      `Customer ${customerDomain} has previously received an advance and should not be in ` +
        `funding-application:company-creation-redirect. Redirecting to financing instead.`
    );
    return <Navigate to={`/${customerDomain}/financing/advances`} />;
  }

  // At this point we only have Sales customers.
  // The active_product_request logic is a self-heal precaution and prevents us from creating multiple Deals
  // if we somehow end up here by accident.

  if (statusData.has_active_product_request) {
    logger.warn(
      `Customer ${customerDomain} has an active Sales product request ` +
        `and ended up in ` +
        `funding-application:company-creation-redirect. Redirecting to home instead.`
    );
    return <Navigate to="/" />;
  }

  if (!statusData.has_previous_advances && !statusData.has_active_product_request) {
    return <CreateFundingApplication />;
  }

  return <FullScreenLoader />;
};

const CreateFundingApplication = () => {
  const { customerDomain } = useStrictParams<{ customerDomain: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    data: { customerUuid },
  } = useCustomerAppConfig();

  const {
    mutate: createFundingApplication,
    isPending,
    isSuccess,
  } = useMutation({
    mutationFn: (data: FundingApplicationSchemas.ConfigureNewFunderCustomerIn) =>
      axiosClient.post(
        FUNDING_APPLICATION_CONFIGURE_NEW_FUNDER_CUSTOMER_API.replace(":customerSlug", customerDomain),
        data
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [CUSTOMER_APP_CONFIG_QUERY_KEY] });
      await queryClient.invalidateQueries({ queryKey: getFinancingStageQueryKey(customerUuid) });
      navigate("/");
    },
    onError: async (error) => {
      if (error.response?.status === 409) {
        // Customer already has an active product request
        await queryClient.invalidateQueries({ queryKey: [CUSTOMER_APP_CONFIG_QUERY_KEY] });
        await queryClient.invalidateQueries({ queryKey: getFinancingStageQueryKey(customerUuid) });
        navigate("/");
      } else {
        window.location.assign("/500");
      }
    },
  });

  useEffect(() => {
    if (isPending || isSuccess) {
      // prevent mutation from being called again
      return;
    }
    const revenue = Cookies.get("revenue");
    createFundingApplication({
      country_of_incorporation: Cookies.get("country-of-incorporation"),
      revenue: revenue ? parseFloat(revenue) : null,
    });
  }, [createFundingApplication, isPending, isSuccess]);

  return <FullScreenLoader />;
};
